import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/UserSettings.css';
import TwoFactorSetup from '../features/auth/TwoFactorSetup';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { useAuth } from '../hooks';

const UserSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const isInitialized = useRef(false);
  const hasUserEdited = useRef(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [show2FASetup, setShow2FASetup] = useState(false);
  
  // Determine active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/security')) return 'security';
    if (path.includes('/preferences')) return 'preferences';
    return 'profile';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTab());

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:7071/api';

  // Update activeTab when location changes
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  // Update form data when currentUser is available and not yet initialized and user hasn't edited
  useEffect(() => {
    if (currentUser && !isInitialized.current && !hasUserEdited.current) {
      setFormData({
        name: currentUser.name || '',
        email: currentUser.email || '',
        company: currentUser.company || '',
        phone: currentUser.phone || '',
        country: currentUser.country || ''
      });
      isInitialized.current = true;
    }
  }, [currentUser]);

  const countries = [
    { code: 'FI', name: 'Finland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'DK', name: 'Denmark' },
    { code: 'NO', name: 'Norway' },
    { code: 'EE', name: 'Estonia' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' }
  ];

  const handleInputChange = (field, value) => {
    hasUserEdited.current = true;
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!currentUser || !currentUser.id) {
        throw new Error('User not logged in. Please log in again.');
      }

      // Call backend API to update user profile
      const response = await fetch(`${apiUrl}/customers/${currentUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          company: formData.company,
          country: formData.country
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Your account was not found. Please sign up again or contact support.');
        }
        throw new Error(data.error || 'Failed to update profile');
      }

      // Update current user with response from backend
      const updatedUser = data.customer;
      
      // Update localStorage
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      
      setSuccess('[SUCCESS] Settings saved successfully!');
    } catch (err) {
      setError('[ERROR] ' + err.message);
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (show2FASetup) {
    return (
      <TwoFactorSetup 
        currentUser={currentUser} 
        onBack={() => setShow2FASetup(false)}
      />
    );
  }

  return (
    <>
      <Header 
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={handleLogout}
      />
      
      <div className="app">
        <Sidebar />
        
        <main className="main-content">
          <div className="content-area">
            {/* Info Banner - Page Style */}
            <div className="page-info-banner">
              <div style={{fontSize: '11px', fontWeight: '600', color: '#8a8886', letterSpacing: '0.5px', marginBottom: '8px'}}>ACCOUNT</div>
              <div className="page-info-title">Account Settings</div>
              <div className="page-info-text">
                Manage your profile, security, and preferences.
              </div>
            </div>

            <div className="settings-container-wrapper">
              <div className="settings-tabs">
                <button 
                  className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('profile'); navigate('/dashboard/settings'); }}
                >
                  Profile
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('security'); navigate('/dashboard/settings/security'); }}
                >
                  Account Security
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'preferences' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('preferences'); navigate('/dashboard/settings/preferences'); }}
                >
                  Preferences
                </button>
              </div>

              <div className="settings-content">
                {activeTab === 'profile' && (
                  <form onSubmit={handleSave} className="settings-form" key={currentUser?.id || 'empty'}>
                    <div className="section-card">
                      <h2 className="section-title">Profile Information</h2>
                      <p className="section-description">Update your personal information and contact details</p>

                      <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="fullname"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="John Doe"
                          autoComplete="name"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="emailaddress"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="john@example.com"
                          autoComplete="email"
                          required
                          disabled
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phonenumber"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="+358 40 123 4567"
                          autoComplete="tel"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="company">Company</label>
                        <input
                          type="text"
                          id="company"
                          name="companyname"
                          value={formData.company}
                          onChange={(e) => handleInputChange('company', e.target.value)}
                          placeholder="Your Company Ltd"
                          autoComplete="organization"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="country">Country *</label>
                        <select
                          id="country"
                          value={formData.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          required
                        >
                          <option value="">Select your country</option>
                          {countries.map(country => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {error && <div className="alert alert-error">{error}</div>}
                      {success && <div className="alert alert-success">{success}</div>}

                      <div className="form-actions">
                        <button type="submit" className="btn-primary" disabled={loading}>
                          {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => navigate('/dashboard')}>
                          Cancel
                        </button>
                      </div>
                    </div>

                    <div className="section-card">
                      <h2 className="section-title">Account Information</h2>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">User ID</span>
                          <span className="info-value">{currentUser?.id}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Account Created</span>
                          <span className="info-value">
                            {currentUser?.created_at ? new Date(currentUser.created_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Last Updated</span>
                          <span className="info-value">
                            {currentUser?.updated_at ? new Date(currentUser.updated_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Verification Status</span>
                          <span className="info-value">
                            {currentUser?.verified ? 'Verified' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </form>
                )}

                {activeTab === 'security' && (
                  <div className="section-card">
                    <h2 className="section-title">Account Security</h2>
                    <p className="section-description">Manage your account security settings</p>

                    <div className="security-section">
                      <h3>Two-Factor Authentication</h3>
                      <p>
                        {currentUser?.totp_enabled 
                          ? 'Two-factor authentication is enabled'
                          : 'Add an extra layer of security to your account with authenticator apps'}
                      </p>
                      <button 
                        className="btn-secondary" 
                        onClick={() => setShow2FASetup(true)}
                      >
                        {currentUser?.totp_enabled ? 'Manage 2FA' : 'Enable 2FA'}
                      </button>
                    </div>

                    <div className="security-section">
                      <h3>Password</h3>
                      <p>Secure your account with a strong password</p>
                      <button className="btn-secondary" disabled>Change Password (Coming Soon)</button>
                    </div>

                    <div className="security-section danger-zone">
                      <h3>Danger Zone</h3>
                      <p>Permanently delete your account and all associated data</p>
                      <button className="btn-danger" disabled>Delete Account (Coming Soon)</button>
                    </div>
                  </div>
                )}

                {activeTab === 'preferences' && (
                  <div className="section-card">
                    <h2 className="section-title">Preferences</h2>
                    <p className="section-description">Customize your experience</p>

                    <div className="form-group">
                      <label>
                        <input type="checkbox" defaultChecked />
                        Email notifications for new compliance updates
                      </label>
                    </div>

                    <div className="form-group">
                      <label>
                        <input type="checkbox" defaultChecked />
                        Weekly compliance reports
                      </label>
                    </div>

                    <div className="form-group">
                      <label>
                        <input type="checkbox" />
                        Marketing communications
                      </label>
                    </div>

                    <div className="form-actions">
                      <button className="btn-primary">Save Preferences</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="settings-footer">
                <button className="btn btn-secondary logout-btn" onClick={handleLogout}>
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserSettings;
